import { Text } from "@clipboard-health/ui-components";
import { Stack } from "@mui/material";
import { differenceInMinutes } from "date-fns";
import { useMemo } from "react";

import { TimeRangeLabel } from "../../DateTime/TimeRangeLabel";
import { formatDurationWithBreakParts } from "../../utils/formatDurationWithBreakParts";

export interface ShiftTimeDetailsProps {
  shiftStartDate: Date;
  shiftEndDate: Date;
  breakDurationInMinutes?: number;
  requiresBreak?: boolean;
  workplaceTimezone: string;
}

export function ShiftTimeDetails(props: ShiftTimeDetailsProps) {
  const {
    shiftStartDate,
    shiftEndDate,
    breakDurationInMinutes = 0,
    requiresBreak = false,
    workplaceTimezone,
  } = props;

  const deviceTimezone = useMemo(() => new Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const totalDurationInHours = useMemo(
    () => differenceInMinutes(shiftEndDate, shiftStartDate) / 60,
    [shiftStartDate, shiftEndDate]
  );

  const durationParts = useMemo(
    () =>
      formatDurationWithBreakParts({ totalDurationInHours, breakDurationInMinutes, requiresBreak }),
    [totalDurationInHours, breakDurationInMinutes, requiresBreak]
  );

  return (
    <Stack spacing={1}>
      <TimeRangeLabel
        semibold
        dateRange={{
          startDate: shiftStartDate,
          endDate: shiftEndDate,
        }}
        timezone={workplaceTimezone}
        deviceTimezone={deviceTimezone}
        variant="h5"
      />
      <Stack direction="row" spacing={2} alignItems="baseline">
        <Text variant="body1">{durationParts.duration}</Text>
        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
          {durationParts.breakInfo}
        </Text>
      </Stack>
    </Stack>
  );
}
